import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./style.module.css";
import legacyStyles from "./legacy.module.css";
import classNames from "classnames";
import { useStyles } from "../../context/stylesContext";

interface Props {
  children: ReactNode;
  /**
   * `legacy` — панель снизу без overlay (только select-tariff, блок со слайдером).
   * `responsive` — по умолчанию: bottom sheet / центр на десктопе (оплата, fail-страницы).
   */
  variant?: "responsive" | "legacy";
}

/**
 * Старая ModalSimple: fixed bottom, slide-up.
 * Высоту панели пишем только в sheetBottomInset (центрирование заголовка над листом) —
 * глобальный фон не двигаем.
 */
function ModalSimpleLegacy({ children }: { children: ReactNode }) {
  const { setStyles } = useStyles();
  const modalRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    const el = modalRef.current;
    if (!el) return;

    const sync = () => {
      el.classList.add(legacyStyles.open);
      const height = el.getBoundingClientRect().height;
      if (height) {
        setStyles({ sheetBottomInset: `${height}px` });
      }
    };

    const frame = requestAnimationFrame(sync);
    const ro = new ResizeObserver(() => sync());
    ro.observe(el);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      setStyles({ sheetBottomInset: "0px" });
    };
  }, [isMounted, setStyles]);

  if (!isMounted) return null;

  return (
    <div ref={modalRef} className={legacyStyles.modalContainer}>
      <div className={classNames(legacyStyles.modal, legacyStyles.glassBorder)}>
        {children}
      </div>
    </div>
  );
}

/** Новая ModalSimple: overlay + центр на ≥768px. Фон не сдвигаем. */
function ModalSimpleResponsive({ children }: { children: ReactNode }) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [animatedOpen, setAnimatedOpen] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimatedOpen(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={classNames(styles.overlay, animatedOpen && styles.open)}
      role="presentation"
    >
      <div ref={sheetRef} className={styles.sheet}>
        <div className={classNames(styles.modal, styles.glassBorder)}>{children}</div>
      </div>
    </div>,
    document.body
  );
}

const ModalSimple = ({ children, variant = "responsive" }: Props) => {
  if (variant === "legacy") {
    return <ModalSimpleLegacy>{children}</ModalSimpleLegacy>;
  }
  return <ModalSimpleResponsive>{children}</ModalSimpleResponsive>;
};

export default ModalSimple;
